import { DateRangeUnit } from '../constants';

export type RelativeDateRange = {
  value: number;
  unit: DateRangeUnit;
};

export type DocumentTypeValidationSettings = {
  validFor?: RelativeDateRange;
  issuedAfter?: RelativeDateRange;
};

const isRelativeDateRange = (value: unknown): value is RelativeDateRange => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.value === 'number' &&
    record.value > 0 &&
    (record.unit === 'DAYS' ||
      record.unit === 'MONTHS' ||
      record.unit === 'YEARS')
  );
};

/**
 * Parses the `validationSettings` AWSJSON string persisted on `DocumentType`
 * into a typed object. Silently returns an empty object on malformed input —
 * we never want the drawer to crash because a legacy row has a stale shape.
 */
export const parseValidationSettings = (
  raw: string | null | undefined
): DocumentTypeValidationSettings => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: DocumentTypeValidationSettings = {};
    if (isRelativeDateRange(parsed.validFor)) result.validFor = parsed.validFor;
    if (isRelativeDateRange(parsed.issuedAfter))
      result.issuedAfter = parsed.issuedAfter;
    return result;
  } catch {
    return {};
  }
};

/**
 * Serialises a typed settings object into the AWSJSON string shape.
 * Returns `null` when nothing is set so the row column stays empty.
 */
export const serialiseValidationSettings = (
  settings: DocumentTypeValidationSettings
): string | null => {
  const payload: DocumentTypeValidationSettings = {};
  if (settings.validFor) payload.validFor = settings.validFor;
  if (settings.issuedAfter) payload.issuedAfter = settings.issuedAfter;
  if (!payload.validFor && !payload.issuedAfter) return null;
  return JSON.stringify(payload);
};
